/**
 * Unified Route Configuration
 *
 * Routes are now permission-based using modulePermissions from the backend.
 * Each route has a moduleKey that maps to the backend's modulePermissions.
 *
 * The PermissionWrapper checks: modulePermissions[moduleKey].canView === true
 *
 * Available moduleKeys from backend (ModuleKey enum):
 * - dashboard, pre_fill, manage_companies, manage_agencies, manage_orders
 * - service_orders, job_orders, candidates, users_roles, payroll
 * - global, compensation_codes, wc_catalogs, paycodes, authorities, tax
 * - company_manage_profile, company_manage_orders, company_invoices, company_timesheets
 * - And submodules like: company_profile, site, bill_codes, etc.
 */

// ============================================================================
// TYPES
// ============================================================================

/**
 * Layout variant determines which sidebar/header to show
 */
export type LayoutVariant =
  | 'platform'
  | 'main-agency'
  | 'msp'
  | 'agency'
  | 'company'
  | 'general-admin'

/**
 * Route configuration interface
 */
export interface RouteConfig {
  /** Unique route identifier */
  id: string
  /** Route path (relative to layout base) */
  path: string
  /** Component to render - string name that maps to COMPONENT_MAP in AppRoutes */
  component: string
  /** moduleKey from backend modulePermissions - if present, PermissionWrapper checks canView */
  moduleKey?: string
  /** Submodule key if this is a nested permission */
  submoduleKey?: string
  /** Which layout variants can access this route (if omitted, all variants) */
  variants?: LayoutVariant[]
  /** Route metadata */
  meta?: {
    title?: string
    description?: string
    /** Whether this is an index/default route */
    index?: boolean
    /** Redirect path for this route */
    redirectTo?: string
  }
}

/**
 * Layout configuration with routes
 */
export interface LayoutConfig {
  variant: LayoutVariant
  basePath: string
  routes: RouteConfig[]
}

// ============================================================================
// UNIFIED ROUTE LIST
// All routes defined once, filtered by variant and permissions at runtime
// ============================================================================

export const ALL_ROUTES: RouteConfig[] = [
  // ============================================================================
  // DASHBOARD ROUTES - Different component per variant
  // ============================================================================
  {
    id: 'platform-dashboard',
    path: 'dashboard',
    component: 'Dashboard',
    moduleKey: 'dashboard',
    variants: ['platform'],
    meta: {title: 'Dashboard'},
  },
  {
    id: 'msp-dashboard',
    path: 'dashboard',
    component: 'MspAgencyDashboard',
    moduleKey: 'dashboard',
    variants: ['main-agency', 'msp'],
    meta: {title: 'Dashboard'},
  },
  {
    id: 'agency-dashboard',
    path: 'dashboard',
    component: 'AgencyDashboard',
    moduleKey: 'dashboard',
    variants: ['agency'],
    meta: {title: 'Dashboard'},
  },
  {
    id: 'company-dashboard',
    path: 'dashboard',
    component: 'CompanyDashboard',
    moduleKey: 'dashboard',
    variants: ['company'],
    meta: {title: 'Dashboard'},
  },

  // ============================================================================
  // MANAGE COMPANIES ROUTES
  // ============================================================================
  {
    id: 'manage-companies',
    path: 'manage-companies',
    component: 'ManageCompaniesPage',
    moduleKey: 'manage_companies',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'general-admin'],
  },
  {
    id: 'company-detail',
    path: 'manage-companies/:companyId',
    component: 'CompanyManagementPage',
    moduleKey: 'manage_companies',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'general-admin'],
  },
  {
    id: 'company-tab',
    path: 'manage-companies/:companyId/:tab',
    component: 'CompanyManagementPage',
    moduleKey: 'manage_companies',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'general-admin'],
  },
  // Company admin routes - use company_manage_profile permission
  {
    id: 'company-admin-detail',
    path: 'manage-companies/:companyId',
    component: 'CompanyManagementPage',
    moduleKey: 'company_manage_profile',
    variants: ['company'],
  },
  {
    id: 'company-admin-tab',
    path: 'manage-companies/:companyId/:tab',
    component: 'CompanyManagementPage',
    moduleKey: 'company_manage_profile',
    variants: ['company'],
  },
  {
    id: 'company-create',
    path: 'manage-companies/create/:tab',
    component: 'CompanyDetailPage',
    moduleKey: 'manage_companies',
    submoduleKey: 'create_company',
    variants: ['platform', 'main-agency', 'general-admin', 'agency', 'msp'],
  },
  // Region routes
  {
    id: 'region-create',
    path: 'manage-companies/:companyId/regions/create',
    component: 'CompanyManagementPage',
    moduleKey: 'manage_companies',
    submoduleKey: 'company_region',
  },
  {
    id: 'region-detail',
    path: 'manage-companies/:companyId/regions/:regionId/:tab',
    component: 'CompanyManagementPage',
    moduleKey: 'manage_companies',
    submoduleKey: 'company_region',
  },
  // Site routes
  {
    id: 'site-create',
    path: 'manage-companies/:companyId/regions/:regionId/sites/create',
    component: 'CompanyManagementPage',
    moduleKey: 'manage_companies',
    submoduleKey: 'site',
  },
  {
    id: 'site-detail',
    path: 'manage-companies/:companyId/regions/:regionId/sites/:siteId/:tab',
    component: 'CompanyManagementPage',
    moduleKey: 'manage_companies',
    submoduleKey: 'site',
  },
  // Position routes (under company)
  {
    id: 'company-position-new',
    path: 'manage-companies/:companyId/positions/new',
    component: 'AddNewPositionPage',
    moduleKey: 'manage_companies',
    submoduleKey: 'company_positions',
  },
  {
    id: 'company-position-edit',
    path: 'manage-companies/:companyId/positions/:positionId/edit',
    component: 'EditPositionPage',
    moduleKey: 'manage_companies',
    submoduleKey: 'company_positions',
  },
  {
    id: 'company-position-template',
    path: 'manage-companies/:companyId/positions/templates/new',
    component: 'AddPositionTemplatePage',
    moduleKey: 'manage_companies',
    submoduleKey: 'company_positions',
    variants: ['platform', 'main-agency', 'msp'],
  },
  // Bill code routes
  {
    id: 'company-billcode-add',
    path: 'manage-companies/:companyId/bill-codes/add',
    component: 'AddBillCodePage',
    moduleKey: 'manage_companies',
    submoduleKey: 'bill_codes',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'general-admin'],
  },
  {
    id: 'company-billcode-edit',
    path: 'manage-companies/:companyId/bill-codes/edit/:billCodeId',
    component: 'AddBillCodePage',
    moduleKey: 'manage_companies',
    submoduleKey: 'bill_codes',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'general-admin'],
  },
  // Company admin bill code routes (different component)
  {
    id: 'company-admin-billcode-add',
    path: 'manage-companies/:companyId/bill-codes/add',
    component: 'CompanyAdminBillCodePage',
    moduleKey: 'company_manage_profile',
    submoduleKey: 'bill_codes',
    variants: ['company'],
  },
  {
    id: 'company-admin-billcode-edit',
    path: 'manage-companies/:companyId/bill-codes/edit/:billCodeId',
    component: 'CompanyAdminBillCodePage',
    moduleKey: 'company_manage_profile',
    submoduleKey: 'bill_codes',
    variants: ['company'],
  },
  // Company role routes (under manage-companies)
  {
    id: 'company-role-add',
    path: 'manage-companies/:companyId/roles/add',
    component: 'AddRolePage',
    moduleKey: 'manage_companies',
    submoduleKey: 'company_users_roles',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'general-admin'],
  },
  {
    id: 'company-role-edit',
    path: 'manage-companies/:companyId/roles/:roleId/edit',
    component: 'EditRolePage',
    moduleKey: 'manage_companies',
    submoduleKey: 'company_users_roles',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'general-admin'],
  },
  {
    id: 'company-role-detail',
    path: 'manage-companies/:companyId/roles/:roleId',
    component: 'RoleDetailPage',
    moduleKey: 'manage_companies',
    submoduleKey: 'company_users_roles',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'general-admin'],
  },
  // Company admin role routes
  {
    id: 'company-admin-role-add',
    path: 'manage-companies/:companyId/roles/add',
    component: 'AddRolePage',
    moduleKey: 'company_manage_profile',
    submoduleKey: 'company_users_roles',
    variants: ['company'],
  },
  {
    id: 'company-admin-role-edit',
    path: 'manage-companies/:companyId/roles/:roleId/edit',
    component: 'EditRolePage',
    moduleKey: 'company_manage_profile',
    submoduleKey: 'company_users_roles',
    variants: ['company'],
  },
  {
    id: 'company-admin-role-detail',
    path: 'manage-companies/:companyId/roles/:roleId',
    component: 'RoleDetailPage',
    moduleKey: 'company_manage_profile',
    submoduleKey: 'company_users_roles',
    variants: ['company'],
  },

  // ============================================================================
  // MANAGE AGENCIES ROUTES
  // ============================================================================
  {
    id: 'manage-agencies',
    path: 'manage-agencies',
    component: 'ManageAgenciesPage',
    moduleKey: 'manage_agencies',
    variants: ['platform', 'main-agency', 'general-admin'],
  },
  {
    id: 'agency-detail',
    path: 'manage-agencies/:agencyId',
    component: 'AgencyManagementPage',
    moduleKey: 'manage_agencies',
    variants: ['platform', 'main-agency', 'msp', 'general-admin'],
  },
  {
    id: 'agency-tab',
    path: 'manage-agencies/:agencyId/:tab',
    component: 'AgencyManagementPage',
    moduleKey: 'manage_agencies',
    variants: ['platform', 'main-agency', 'msp', 'general-admin'],
  },
  // Agency admin routes - use manage_profile permission
  {
    id: 'agency-admin-detail',
    path: 'manage-agencies/:agencyId',
    component: 'AgencyManagementPage',
    moduleKey: 'manage_profile',
    variants: ['agency'],
  },
  {
    id: 'agency-admin-tab',
    path: 'manage-agencies/:agencyId/:tab',
    component: 'AgencyManagementPage',
    moduleKey: 'manage_profile',
    variants: ['agency'],
  },
  {
    id: 'agency-catalog-detail',
    path: 'manage-agencies/:agencyId/catalogs/:catalogId/:tab?',
    component: 'CatalogCompensationCodesPage',
    moduleKey: 'manage_agencies',
    submoduleKey: 'catalogs',
  },
  {
    id: 'agency-branch-new',
    path: 'manage-agencies/:agencyId/branches/new',
    component: 'AgencyManagementPage',
    moduleKey: 'manage_agencies',
    variants: ['platform', 'main-agency', 'general-admin'],
  },
  {
    id: 'agency-branch-detail',
    path: 'manage-agencies/:agencyId/branches/:branchId/:tab?',
    component: 'AgencyManagementPage',
    moduleKey: 'manage_agencies',
  },
  // Agency role routes (under manage-agencies)
  {
    id: 'agency-role-add',
    path: 'manage-agencies/:agencyId/roles/add',
    component: 'AddRolePage',
    moduleKey: 'manage_agencies',
    submoduleKey: 'agency_users_roles',
    variants: ['platform', 'main-agency', 'msp', 'general-admin'],
  },
  {
    id: 'agency-role-edit',
    path: 'manage-agencies/:agencyId/roles/:roleId/edit',
    component: 'EditRolePage',
    moduleKey: 'manage_agencies',
    submoduleKey: 'agency_users_roles',
    variants: ['platform', 'main-agency', 'msp', 'general-admin'],
  },
  {
    id: 'agency-role-detail',
    path: 'manage-agencies/:agencyId/roles/:roleId',
    component: 'RoleDetailPage',
    moduleKey: 'manage_agencies',
    submoduleKey: 'agency_users_roles',
    variants: ['platform', 'main-agency', 'msp', 'general-admin'],
  },
  // Agency admin role routes (for agency variant - their own agency)
  {
    id: 'agency-admin-role-add',
    path: 'manage-agencies/:agencyId/roles/add',
    component: 'AddRolePage',
    moduleKey: 'manage_profile',
    submoduleKey: 'agency_users_roles',
    variants: ['agency'],
  },
  {
    id: 'agency-admin-role-edit',
    path: 'manage-agencies/:agencyId/roles/:roleId/edit',
    component: 'EditRolePage',
    moduleKey: 'manage_profile',
    submoduleKey: 'agency_users_roles',
    variants: ['agency'],
  },
  {
    id: 'agency-admin-role-detail',
    path: 'manage-agencies/:agencyId/roles/:roleId',
    component: 'RoleDetailPage',
    moduleKey: 'manage_profile',
    submoduleKey: 'agency_users_roles',
    variants: ['agency'],
  },
  {
    id: 'agency-new',
    path: 'manage-agencies/new',
    component: 'OnboardingAgency',
    moduleKey: 'manage_agencies',
    variants: ['platform', 'general-admin'],
  },
  // Agency profile redirect (for agency variant - their own profile)
  {
    id: 'agency-profile-redirect',
    path: 'manage-profile',
    component: 'AgencyProfileRedirect',
    moduleKey: 'manage_profile',
    variants: ['agency'],
  },

  // ============================================================================
  // SERVICE ORDER ROUTES
  // ============================================================================
  // Agency/MSP service order routes
  {
    id: 'agency-service-orders',
    path: 'manage-orders',
    component: 'AgencyManageOrdersMain',
    moduleKey: 'manage_orders',
    submoduleKey: 'service_orders',
    variants: ['main-agency', 'msp', 'agency'],
  },
  {
    id: 'agency-service-order-add',
    path: 'manage-orders/add-order',
    component: 'NewServiceOrderFormPage',
    moduleKey: 'manage_orders',
    submoduleKey: 'service_orders',
    variants: ['platform', 'main-agency', 'msp', 'agency'],
  },
  {
    id: 'agency-service-order-edit',
    path: 'manage-orders/edit-service-order/:serviceOrderId',
    component: 'NewServiceOrderFormPage',
    moduleKey: 'manage_orders',
    submoduleKey: 'service_orders',
    variants: ['platform', 'main-agency', 'msp', 'agency'],
  },
  {
    id: 'agency-service-order-details',
    path: 'manage-orders/:orderId',
    component: 'ServiceOrderDetailsPage',
    moduleKey: 'manage_orders',
    submoduleKey: 'service_orders',
    variants: ['platform', 'main-agency', 'msp', 'agency'],
  },
  {
    id: 'agency-service-order-pending',
    path: 'manage-orders/pending',
    component: 'AgencyPendingOrders',
    moduleKey: 'manage_orders',
    submoduleKey: 'service_orders',
    variants: ['main-agency', 'msp', 'agency'],
  },
  {
    id: 'agency-service-order-pending-detail',
    path: 'manage-orders/pending/:serviceOrderId',
    component: 'ServiceOrderDetailsPage',
    moduleKey: 'manage_orders',
    submoduleKey: 'service_orders',
    variants: ['main-agency', 'msp', 'agency'],
  },
  {
    id: 'agency-service-order-completed',
    path: 'manage-orders/completedOrders',
    component: 'CompletedOrdersMain',
    moduleKey: 'manage_orders',
    submoduleKey: 'service_orders',
    variants: ['main-agency', 'msp', 'agency'],
  },
  {
    id: 'agency-service-order-summary',
    path: 'manage-orders/:orderId/:type/summary',
    component: 'ServiceOrderSummary',
    moduleKey: 'manage_orders',
    submoduleKey: 'service_orders',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'company'],
  },
  {
    id: 'agency-candidate-distribution',
    path: 'manage-orders/add-order/candidate-distribution',
    component: 'CandidateDistributionPageWrapper',
    moduleKey: 'manage_orders',
    submoduleKey: 'service_orders',
    variants: ['platform', 'main-agency', 'msp', 'agency'],
  },
  {
    id: 'agency-candidate-distribution-position',
    path: 'manage-orders/:serviceOrderId/positions/:positionId/distribute',
    component: 'CandidateDistributionPageWrapper',
    moduleKey: 'manage_orders',
    submoduleKey: 'service_orders',
    variants: ['platform', 'main-agency', 'msp', 'agency'],
  },

  // Company service order routes (different components)
  {
    id: 'company-service-orders',
    path: 'manage-orders',
    component: 'ManageOrders',
    moduleKey: 'company_manage_orders',
    submoduleKey: 'service_orders',
    variants: ['company'],
  },
  {
    id: 'company-service-order-add',
    path: 'manage-orders/add-order',
    component: 'NewServiceOrderFormPage',
    moduleKey: 'company_manage_orders',
    submoduleKey: 'service_orders',
    variants: ['company'],
  },
  {
    id: 'company-service-order-edit',
    path: 'manage-orders/edit-service-order/:serviceOrderId',
    component: 'NewServiceOrderFormPage',
    moduleKey: 'company_manage_orders',
    submoduleKey: 'service_orders',
    variants: ['company'],
  },
  {
    id: 'company-service-order-details',
    path: 'manage-orders/:orderId',
    component: 'OrderDetails',
    moduleKey: 'company_manage_orders',
    submoduleKey: 'service_orders',
    variants: ['company'],
  },
  {
    id: 'company-service-order-completed',
    path: 'manage-orders/completedOrders',
    component: 'CompanyCompletedOrdersMain',
    moduleKey: 'company_manage_orders',
    submoduleKey: 'service_orders',
    variants: ['company'],
  },
  {
    id: 'company-candidate-list',
    path: 'manage-orders/candidate-list/:orderId',
    component: 'CompanyCandidateViewMain',
    moduleKey: 'company_manage_orders',
    submoduleKey: 'job_orders',
    variants: ['company'],
  },
  {
    id: 'company-candidate-distribution',
    path: 'manage-orders/add-order/candidate-distribution',
    component: 'CandidateDistributionPageWrapper',
    moduleKey: 'company_manage_orders',
    submoduleKey: 'service_orders',
    variants: ['company'],
  },
  {
    id: 'company-candidate-distribution-edit',
    path: 'manage-orders/edit-service-order/:serviceOrderId/candidate-distribution',
    component: 'CandidateDistributionPageWrapper',
    moduleKey: 'company_manage_orders',
    submoduleKey: 'service_orders',
    variants: ['company'],
  },

  // ============================================================================
  // JOB ORDER ROUTES (Agency only)
  // ============================================================================
  {
    id: 'job-orders',
    path: 'manage-job-orders',
    component: 'MspAgencyJobOrderMain',
    moduleKey: 'manage_orders',
    submoduleKey: 'job_orders',
    variants: ['main-agency', 'msp', 'agency'],
  },
  {
    id: 'job-order-details',
    path: 'manage-job-orders/:orderId',
    component: 'MspJobOrdersDetailsMain',
    moduleKey: 'manage_orders',
    submoduleKey: 'job_orders',
    variants: ['main-agency', 'msp', 'agency'],
  },
  {
    id: 'job-order-summary',
    path: 'manage-job-orders/summary/:id',
    component: 'JobOrdersDetailsSummaryMain',
    moduleKey: 'manage_orders',
    submoduleKey: 'job_orders',
    variants: ['main-agency', 'msp', 'agency'],
  },
  {
    id: 'job-order-from-prefill',
    path: 'jobOrderPrefill/:jobOrderId/:prefillId',
    component: 'JobOrderFromPrefill',
    moduleKey: 'manage_orders',
    submoduleKey: 'job_orders',
    variants: ['main-agency', 'msp', 'agency'],
  },

  // ============================================================================
  // PREFILL ROUTES (Agency only)
  // ============================================================================
  {
    id: 'prefill',
    path: 'prefill/:prefillCardId',
    component: 'PrefillMain',
    moduleKey: 'pre_fill',
    variants: ['main-agency', 'msp', 'agency'],
  },
  {
    id: 'prefill-add',
    path: 'prefill-add',
    component: 'AddOrEditPrefill',
    moduleKey: 'pre_fill',
    variants: ['main-agency', 'msp', 'agency'],
  },
  {
    id: 'prefill-edit',
    path: 'prefill-edit',
    component: 'AddOrEditPrefill',
    moduleKey: 'pre_fill',
    variants: ['main-agency', 'msp', 'agency'],
  },

  // ============================================================================
  // CANDIDATES ROUTES
  // ============================================================================
  {
    id: 'candidates',
    path: 'candidates',
    component: 'Candidates',
    moduleKey: 'candidates',
  },

  // ============================================================================
  // USERS & ROLES ROUTES
  // ============================================================================
  {
    id: 'users-roles',
    path: 'users-and-roles',
    component: 'UsersAndRoles',
    moduleKey: 'users_roles',
    variants: ['msp', 'agency', 'company'],
  },
  {
    id: 'platform-general-users',
    path: 'users-and-roles/general-users',
    component: 'GeneralUsers',
    moduleKey: 'users_roles',
    variants: ['platform'],
  },
  {
    id: 'platform-general-admins',
    path: 'users-and-roles/general-admins',
    component: 'GeneralAdmins',
    moduleKey: 'users_roles',
    variants: ['platform'],
  },
  {
    id: 'platform-add-admin',
    path: 'users-and-roles/general-admins/add-admin',
    component: 'AddOrEditGa',
    moduleKey: 'users_roles',
    variants: ['platform'],
  },
  {
    id: 'platform-edit-admin',
    path: 'users-and-roles/general-admins/edit-admin',
    component: 'AddOrEditGa',
    moduleKey: 'users_roles',
    variants: ['platform'],
  },
  // NEW: Users & Roles top navigation page (all views)
  {
    id: 'users-roles-page',
    path: 'users-roles',
    component: 'UsersRolesPage',
    moduleKey: 'users_roles',
    variants: ['platform', 'msp', 'main-agency', 'agency', 'company'],
  },
  {
    id: 'users-roles-add-user',
    path: 'users-roles/add-user',
    component: 'AddNewUserPage',
    moduleKey: 'users_roles',
    variants: ['platform', 'msp', 'main-agency', 'agency', 'company'],
  },
  {
    id: 'users-roles-edit-user',
    path: 'users-roles/users/:userId/edit',
    component: 'EditUserPage', // Wrapper that fetches user data and renders AddNewUserPage
    moduleKey: 'users_roles',
    variants: ['platform', 'msp', 'main-agency', 'agency', 'company'],
  },
  {
    id: 'users-roles-add-role',
    path: 'users-roles/roles/add',
    component: 'AddRolePage',
    moduleKey: 'users_roles',
    submoduleKey: 'roles',
    variants: ['platform', 'msp', 'main-agency', 'agency', 'company'],
  },
  {
    id: 'users-roles-edit-role',
    path: 'users-roles/roles/:roleId/edit',
    component: 'EditRolePage',
    moduleKey: 'users_roles',
    submoduleKey: 'roles',
    variants: ['platform', 'msp', 'main-agency', 'agency', 'company'],
  },
  {
    // Handles both detail view and users view via ?view=users query param
    id: 'users-roles-role-detail',
    path: 'users-roles/roles/:roleId',
    component: 'RoleDetailPage',
    moduleKey: 'users_roles',
    submoduleKey: 'roles',
    variants: ['platform', 'msp', 'main-agency', 'agency', 'company'],
  },
  // Global: Roles and Permissions page (category tabs: Platform/MSP/Agency/Company)
  // Note: general-admin has 'roles_permissions' as submodule of 'global', not 'users_roles'
  // The hasModulePermission function now searches submodules across all modules
  {
    id: 'roles-permissions',
    path: 'roles-permissions',
    component: 'GlobalRolesPermissionsPage',
    moduleKey: 'roles_permissions', // Changed to work with both users_roles.roles and global.roles_permissions
    variants: ['platform', 'msp', 'main-agency', 'agency', 'company', 'general-admin'],
  },
  // Add/Edit role from Global Roles page
  {
    id: 'roles-permissions-add',
    path: 'roles-permissions/add',
    component: 'AddRolePage',
    moduleKey: 'roles_permissions',
    variants: ['platform', 'msp', 'main-agency', 'agency', 'company', 'general-admin'],
  },
  {
    id: 'roles-permissions-edit',
    path: 'roles-permissions/:roleId/edit',
    component: 'EditRolePage',
    moduleKey: 'roles_permissions',
    variants: ['platform', 'msp', 'main-agency', 'agency', 'company', 'general-admin'],
  },
  {
    id: 'roles-permissions-detail',
    path: 'roles-permissions/:roleId',
    component: 'RoleDetailPage',
    moduleKey: 'roles_permissions',
    variants: ['platform', 'msp', 'main-agency', 'agency', 'company', 'general-admin'],
  },

  // ============================================================================
  // GLOBAL ROUTES (Platform only)
  // ============================================================================
  {
    id: 'global-compensation-codes',
    path: 'global/compensation-codes',
    component: 'CompensationCodesPage',
    moduleKey: 'global',
    submoduleKey: 'compensation_codes',
    variants: ['platform'],
  },
  {
    id: 'global-wc-catalogs',
    path: 'global/wc-catalogs',
    component: 'WCCatalogsPage',
    moduleKey: 'global',
    submoduleKey: 'wc_catalogs',
    variants: ['platform'],
  },
  {
    id: 'global-wc-catalog-codes',
    path: 'global/wc-catalogs/compensation-codes/:catalogId/:tab?',
    component: 'CatalogCompensationCodesPage',
    moduleKey: 'global',
    submoduleKey: 'wc_catalogs',
    variants: ['platform'],
  },
  {
    id: 'global-paycodes',
    path: 'global/paycodes',
    component: 'PaycodesPage',
    moduleKey: 'global',
    submoduleKey: 'paycodes',
    variants: ['platform'],
  },
  {
    id: 'global-paycode-create',
    path: 'global/paycodes/create',
    component: 'PaycodeFormPage',
    moduleKey: 'global',
    submoduleKey: 'paycodes',
    variants: ['platform'],
  },
  {
    id: 'global-paycode-edit',
    path: 'global/paycodes/edit/:paycodeId',
    component: 'PaycodeFormPage',
    moduleKey: 'global',
    submoduleKey: 'paycodes',
    variants: ['platform'],
  },
  {
    id: 'global-authorities',
    path: 'global/authorities',
    component: 'AuthoritiesPage',
    moduleKey: 'global',
    submoduleKey: 'authorities',
    variants: ['platform'],
  },
  {
    id: 'global-tax',
    path: 'global/tax',
    component: 'TaxManagementPage',
    moduleKey: 'global',
    submoduleKey: 'tax',
    variants: ['platform'],
  },
  {
    id: 'global-metrics',
    path: 'global/metrics',
    component: 'MetricsDashboardPage',
    moduleKey: 'global',
    submoduleKey: 'metrics',
    variants: ['platform'],
  },
  {
    id: 'global-activity-log',
    path: 'global/activity-log',
    component: 'ActivityLogsPage',
    variants: ['platform'],
  },
  // Main agency also gets some global routes
  {
    id: 'main-agency-wc-catalogs',
    path: 'global/wc-catalogs',
    component: 'WCCatalogsPage',
    moduleKey: 'manage_agencies',
    submoduleKey: 'catalogs',
    variants: ['main-agency'],
  },
  {
    id: 'main-agency-paycodes',
    path: 'global/paycodes',
    component: 'PaycodesPage',
    moduleKey: 'manage_agencies',
    submoduleKey: 'payroll_setup',
    variants: ['main-agency'],
  },

  // ============================================================================
  // PAYROLL ROUTES (Platform only)
  // ============================================================================
  {
    id: 'payroll',
    path: 'payroll',
    component: 'PayrollManagementPage',
    moduleKey: 'payroll',
    variants: ['platform'],
  },
  {
    id: 'payroll-reports',
    path: 'payroll/reports',
    component: 'PayrollReportsPage',
    moduleKey: 'payroll',
    submoduleKey: 'generate_report',
    variants: ['platform'],
  },
  {
    id: 'payroll-eor',
    path: 'payroll/:eorId/:view?/:batchId?',
    component: 'PayrollManagementPage',
    moduleKey: 'payroll',
    variants: ['platform'],
  },

  // ============================================================================
  // DATA ROUTES
  // ============================================================================
  {
    id: 'import',
    path: 'import',
    component: 'ImportWizardMain',
    variants: ['platform', 'main-agency'],
  },
  {
    id: 'data-migration',
    path: 'data-migration',
    component: 'DataMigrationPage',
    variants: ['platform', 'main-agency'],
  },
  {
    id: 'data-cleanup',
    path: 'data-cleanup/*',
    component: 'MspDataCleanupMain',
    variants: ['main-agency', 'agency'],
  },

  // ============================================================================
  // COMPANY-SPECIFIC ROUTES
  // ============================================================================
  {
    id: 'company-positions',
    path: 'manage-positions',
    component: 'ManagePositions',
    moduleKey: 'company_manage_profile',
    submoduleKey: 'company_positions',
    variants: ['company'],
  },
  {
    id: 'company-add-position',
    path: 'manage-positions/add-position',
    component: 'AddNewPositionPage',
    moduleKey: 'company_manage_profile',
    submoduleKey: 'company_positions',
    variants: ['company'],
  },
  {
    id: 'company-edit-position',
    path: 'manage-positions/edit-position/:orderId',
    component: 'EditPositionPage',
    moduleKey: 'company_manage_profile',
    submoduleKey: 'company_positions',
    variants: ['company'],
  },
  {
    id: 'company-templates',
    path: 'manage-templates',
    component: 'ManageTemplates',
    moduleKey: 'other_settings',
    variants: ['company'],
  },
  {
    id: 'company-invoices',
    path: 'invoices',
    component: 'InvoicesPage',
    moduleKey: 'company_invoices',
    variants: ['company'],
  },
  {
    id: 'company-timesheets',
    path: 'timesheets',
    component: 'TimesheetPendingView',
    moduleKey: 'company_timesheets',
    variants: ['company'],
  },
  {
    id: 'company-profile-redirect',
    path: 'manage-company',
    component: 'CompanyProfileRedirect',
    moduleKey: 'company_manage_profile',
    variants: ['company'],
  },
  {
    id: 'company-manage-profile',
    path: 'manage-profile',
    component: 'CompanyProfileRedirect',
    moduleKey: 'manage_profile',
    variants: ['company'],
  },

  // ============================================================================
  // MSP-SPECIFIC ROUTES
  // ============================================================================
  {
    id: 'msp-client-positions',
    path: 'client-positions',
    component: 'ManageClientPositions',
    moduleKey: 'manage_companies',
    submoduleKey: 'company_positions',
    variants: ['msp'],
  },
  {
    id: 'msp-templates',
    path: 'manage-templates',
    component: 'MspManageTemplates',
    variants: ['msp'],
  },
  // Note: MSP/platform admins don't have a "Manage Profile" menu item
  // They access agencies via "Manage Agencies" instead

  // ============================================================================
  // MAIN AGENCY SPECIFIC ROUTES
  // ============================================================================
  {
    id: 'main-agency-managed-sites',
    path: 'managed-sites',
    component: 'ManagedSitesPage',
    moduleKey: 'manage_agencies',
    submoduleKey: 'managed_sites',
    variants: ['main-agency'],
  },
  {
    id: 'main-agency-manage-profile',
    path: 'manage-profile',
    component: 'AgencyProfileRedirect',
    moduleKey: 'manage_profile',
    variants: ['main-agency'],
  },
  {
    id: 'main-agency-manage-agency',
    path: 'manage-agency',
    component: 'AgencyProfileRedirect',
    moduleKey: 'manage_profile',
    variants: ['main-agency', 'agency'],
  },

  // ============================================================================
  // ONBOARDING ROUTES (Platform only)
  // ============================================================================
  {
    id: 'onboarding-agency',
    path: 'onboarding-agency',
    component: 'OnboardingAgency',
    moduleKey: 'manage_agencies',
    variants: ['platform'],
  },
  {
    id: 'onboarding-company',
    path: 'onboarding-company',
    component: 'OnboardingCompany',
    moduleKey: 'manage_companies',
    submoduleKey: 'create_company',
    variants: ['platform'],
  },

  // ============================================================================
  // INDEX/REDIRECT ROUTES
  // ============================================================================
  {
    id: 'platform-index',
    path: '',
    component: 'Navigate',
    variants: ['platform'],
    meta: {index: true, redirectTo: 'manage-companies'},
  },
  {
    id: 'main-agency-index',
    path: '',
    component: 'Navigate',
    variants: ['main-agency'],
    meta: {index: true, redirectTo: 'manage-companies'},
  },
  {
    id: 'msp-index',
    path: '',
    component: 'Navigate',
    variants: ['msp'],
    meta: {index: true, redirectTo: 'manage-companies'},
  },
  {
    id: 'agency-index',
    path: '',
    component: 'Navigate',
    variants: ['agency'],
    meta: {index: true, redirectTo: 'manage-companies'},
  },
  {
    id: 'company-index',
    path: '',
    component: 'Navigate',
    variants: ['company'],
    meta: {index: true, redirectTo: 'manage-orders'},
  },
]

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Gets routes for a specific layout variant
 * Filters ALL_ROUTES based on variant
 */
export const getRoutesForVariant = (variant: LayoutVariant): RouteConfig[] => {
  return ALL_ROUTES.filter((route) => {
    // If no variants specified, route is available to all
    if (!route.variants) return true
    // Otherwise, check if variant is in the allowed list
    return route.variants.includes(variant)
  })
}

/**
 * Gets layout configuration for a variant
 */
export const getLayoutConfig = (variant: LayoutVariant): LayoutConfig => {
  const basePaths: Record<LayoutVariant, string> = {
    platform: '/platform-admin',
    'general-admin': '/general-admin',
    'main-agency': '/main-agency-admin',
    msp: '/msp-admin',
    agency: '/agency',
    company: '/company-admin',
  }

  return {
    variant,
    basePath: basePaths[variant],
    routes: getRoutesForVariant(variant),
  }
}

// ============================================================================
// LEGACY EXPORTS (for backward compatibility during migration)
// ============================================================================

export const PLATFORM_ADMIN_CONFIG = getLayoutConfig('platform')
export const MAIN_AGENCY_ADMIN_CONFIG = getLayoutConfig('main-agency')
export const MSP_AGENCY_ADMIN_CONFIG = getLayoutConfig('msp')
export const AGENCY_ADMIN_CONFIG = getLayoutConfig('agency')
export const COMPANY_ADMIN_CONFIG = getLayoutConfig('company')

export const ALL_LAYOUT_CONFIGS: LayoutConfig[] = [
  PLATFORM_ADMIN_CONFIG,
  MAIN_AGENCY_ADMIN_CONFIG,
  MSP_AGENCY_ADMIN_CONFIG,
  AGENCY_ADMIN_CONFIG,
  COMPANY_ADMIN_CONFIG,
]

/**
 * Gets layout config by base path
 */
export const getLayoutConfigByPath = (path: string): LayoutConfig | undefined => {
  return ALL_LAYOUT_CONFIGS.find((config) => path.startsWith(config.basePath))
}
